import Hls from 'hls.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

type AuthUser = {
  user_id?: string;
  email?: string;
  name?: string;
  role?: string;
  plan_id?: string;
  avatar_url?: string;
};

type AuthSnapshot = {
  token: string;
  expires: string;
  user: AuthUser;
};

type StreamItem = {
  stream_id: string;
  status: string;
  video_source_url?: string;
  file_name?: string;
  thumbnail_url?: string;
  worker_play_url?: string;
  duration_seconds: number;
  file_size_bytes: number;
  created_at: string;
};

type ApiKeyItem = {
  key_id: string;
  name: string;
  value_preview: string;
  created_at: string;
};

type AppSection = 'overview' | 'streams' | 'api-keys' | 'settings';
type RepeatMode = 'next' | 'one' | 'all';

function readAuth(): AuthSnapshot {
  try {
    const userRaw = localStorage.getItem('auth_user') || '{}';
    const user = JSON.parse(userRaw) as AuthUser;
    return {
      token: localStorage.getItem('auth_token') || '',
      expires: localStorage.getItem('auth_token_expires') || '',
      user,
    };
  } catch {
    return {
      token: '',
      expires: '',
      user: {},
    };
  }
}

function trimEmail(v: string): string {
  const s = String(v || '').trim();
  if (!s) {
    return 'anonymous@nxdot.com';
  }
  return s.length > 22 ? s.slice(0, 19) + '...' : s;
}

function formatBytes(v: number): string {
  if (!Number.isFinite(v) || v <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = v;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function formatDuration(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}h ${m}m ${s}s`;
  }
  if (m > 0) {
    return `${m}m ${s}s`;
  }
  return `${s}s`;
}

function formatClock(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function sectionFromPath(pathname: string): AppSection {
  const p = String(pathname || '').toLowerCase();
  if (p === '/app/streams') {
    return 'streams';
  }
  if (p === '/app/api-keys') {
    return 'api-keys';
  }
  if (p === '/app/settings') {
    return 'settings';
  }
  return 'overview';
}

function pathFromSection(section: AppSection): string {
  if (section === 'streams') {
    return '/app/streams';
  }
  if (section === 'api-keys') {
    return '/app/api-keys';
  }
  if (section === 'settings') {
    return '/app/settings';
  }
  return '/app';
}

function LogoMark(): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/25">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M2 12h4l2-4 3 8 2-4h9" stroke="#022c22" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="text-lg font-semibold tracking-tight text-slate-100">
        <span className="text-slate-200">NX</span><span className="text-emerald-300">stream</span>
      </div>
    </div>
  );
}

function buildMockApiKeys(): ApiKeyItem[] {
  return Array.from({ length: 38 }, (_, idx) => {
    const n = idx + 1;
    return {
      key_id: `key_${String(n).padStart(3, '0')}`,
      name: n % 2 === 0 ? `Server-${n}` : `Frontend-${n}`,
      value_preview: `sk_live_${String(n).padStart(2, '0')}...${(n * 17).toString(16)}`,
      created_at: new Date(Date.now() - n * 86400 * 1000).toISOString(),
    };
  });
}

function FloatingVideoPlayer(props: {
  queue: StreamItem[];
  currentIndex: number;
  onIndexChange: (next: number) => void;
  onClose: () => void;
}): JSX.Element | null {
  const { queue, currentIndex, onIndexChange, onClose } = props;
  const current = queue[currentIndex];

  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showControls, setShowControls] = useState<boolean>(true);
  const [repeatMode, setRepeatMode] = useState<RepeatMode>('next');
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [sourceURL, setSourceURL] = useState<string>('');
  const [error, setError] = useState<string>('');

  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    const w = Math.min(Math.max(window.innerWidth - 32, 220), 320);
    const h = Math.round((w * 16) / 9);
    return {
      x: Math.max(12, window.innerWidth - w - 16),
      y: Math.max(70, window.innerHeight - h - 100),
    };
  });

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const hideControlsTimerRef = useRef<number | null>(null);
  const dragRef = useRef<{ active: boolean; offsetX: number; offsetY: number }>({ active: false, offsetX: 0, offsetY: 0 });

  const bumpControls = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimerRef.current !== null) {
      window.clearTimeout(hideControlsTimerRef.current);
    }
    hideControlsTimerRef.current = window.setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  const stopAutoHide = useCallback(() => {
    if (hideControlsTimerRef.current !== null) {
      window.clearTimeout(hideControlsTimerRef.current);
      hideControlsTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resolveSource(): Promise<void> {
      setSourceURL('');
      setError('');
      setCurrentTime(0);
      setDuration(0);

      if (!current) {
        return;
      }

      let nextSource = String(current.video_source_url || '');
      if (current.worker_play_url) {
        try {
          const resp = await fetch(current.worker_play_url, { method: 'GET' });
          if (resp.ok) {
            const payload = (await resp.json()) as { manifest_url?: string };
            const manifestURL = String(payload.manifest_url || '').trim();
            if (manifestURL) {
              nextSource = manifestURL;
            }
          }
        } catch {
          // Keep fallback source URL when worker endpoint is unreachable.
        }
      }

      if (cancelled) {
        return;
      }
      if (!nextSource) {
        setError('No playable source was found for this stream.');
        return;
      }

      setSourceURL(nextSource);
      bumpControls();
    }

    resolveSource();
    return () => {
      cancelled = true;
    };
  }, [current, bumpControls]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !sourceURL) {
      return;
    }

    setError('');
    video.muted = isMuted;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = sourceURL;
    } else if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hlsRef.current = hls;
      hls.loadSource(sourceURL);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          setError('Playback failed to start.');
        }
      });
    } else {
      video.src = sourceURL;
    }

    const startPlayback = async () => {
      try {
        await video.play();
      } catch {
        // Autoplay can be blocked by browser policy.
      }
    };
    startPlayback();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [sourceURL, isMuted]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const onTimeUpdate = () => setCurrentTime(video.currentTime || 0);
    const onDuration = () => setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      if (repeatMode === 'one') {
        video.currentTime = 0;
        void video.play();
        return;
      }

      if (queue.length === 0) {
        return;
      }

      const hasNext = currentIndex < queue.length - 1;
      if (hasNext) {
        onIndexChange(currentIndex + 1);
        return;
      }

      if (repeatMode === 'all') {
        onIndexChange(0);
      }
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('durationchange', onDuration);
    video.addEventListener('loadedmetadata', onDuration);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('durationchange', onDuration);
      video.removeEventListener('loadedmetadata', onDuration);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
    };
  }, [currentIndex, onIndexChange, queue.length, repeatMode]);

  useEffect(() => {
    return () => {
      stopAutoHide();
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, [stopAutoHide]);

  const onDragMouseMove = useCallback((ev: MouseEvent) => {
    if (!dragRef.current.active || isFullscreen) {
      return;
    }
    const width = isMinimized ? 220 : Math.min(Math.max(window.innerWidth - 32, 220), 320);
    const height = isMinimized ? 132 : Math.round((width * 16) / 9) + 92;

    const nextX = clamp(ev.clientX - dragRef.current.offsetX, 8, Math.max(8, window.innerWidth - width - 8));
    const nextY = clamp(ev.clientY - dragRef.current.offsetY, 56, Math.max(56, window.innerHeight - height - 8));
    setPosition({ x: nextX, y: nextY });
  }, [isFullscreen, isMinimized]);

  const onDragMouseUp = useCallback(() => {
    dragRef.current.active = false;
    window.removeEventListener('mousemove', onDragMouseMove);
    window.removeEventListener('mouseup', onDragMouseUp);
  }, [onDragMouseMove]);

  const beginDrag = (ev: React.MouseEvent<HTMLDivElement>) => {
    if (isFullscreen) {
      return;
    }
    dragRef.current = {
      active: true,
      offsetX: ev.clientX - position.x,
      offsetY: ev.clientY - position.y,
    };
    window.addEventListener('mousemove', onDragMouseMove);
    window.addEventListener('mouseup', onDragMouseUp);
  };

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      await video.play().catch(() => {});
    } else {
      video.pause();
    }
    bumpControls();
  };

  const goPrev = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.currentTime > 5) {
      video.currentTime = 0;
      bumpControls();
      return;
    }
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
      return;
    }
    if (repeatMode === 'all' && queue.length > 0) {
      onIndexChange(queue.length - 1);
    }
  };

  const goNext = () => {
    if (currentIndex < queue.length - 1) {
      onIndexChange(currentIndex + 1);
      return;
    }
    if (repeatMode === 'all' && queue.length > 0) {
      onIndexChange(0);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !isMuted;
    }
    setIsMuted((v) => !v);
    bumpControls();
  };

  const toggleFullscreen = async () => {
    const shell = containerRef.current;
    if (!shell) {
      return;
    }
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => {});
    } else {
      await shell.requestFullscreen().catch(() => {});
    }
    bumpControls();
  };

  const cycleRepeat = () => {
    setRepeatMode((mode) => {
      if (mode === 'next') {
        return 'one';
      }
      if (mode === 'one') {
        return 'all';
      }
      return 'next';
    });
    bumpControls();
  };

  if (!current) {
    return null;
  }

  const defaultWidth = Math.min(Math.max(window.innerWidth - 32, 220), 320);
  const frameWidth = isMinimized ? 220 : defaultWidth;
  const frameHeight = isMinimized ? 132 : Math.round((frameWidth * 16) / 9);
  const headerHeight = 30;

  const playerStyle: React.CSSProperties = isFullscreen
    ? {
        width: '100vw',
        height: '100vh',
        left: '0px',
        top: '0px',
      }
    : {
        width: `${frameWidth}px`,
        left: `${position.x}px`,
        top: `${position.y}px`,
      };

  const videoShellStyle: React.CSSProperties = isFullscreen
    ? { height: `calc(100vh - ${headerHeight}px)` }
    : { height: `${frameHeight}px` };

  return (
    <div
      ref={containerRef}
      className={`fixed z-40 overflow-hidden border border-slate-700 bg-slate-900/95 shadow-2xl shadow-black/50 ${isFullscreen ? 'rounded-none' : 'rounded-2xl'}`}
      style={playerStyle}
      onMouseMove={bumpControls}
    >
      <div className="flex cursor-move items-center justify-end border-b border-slate-700 bg-slate-900/95 px-2 py-1" onMouseDown={beginDrag}>
        <div className="flex items-center gap-1" onMouseDown={(e) => e.stopPropagation()}>
          <button type="button" onClick={toggleFullscreen} className="rounded border border-slate-600 px-2 py-0.5 text-[11px] text-slate-200 hover:bg-slate-800">{isFullscreen ? 'Exit' : 'Full'}</button>
          <button type="button" onClick={() => setIsMinimized((v) => !v)} className="rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800">{isMinimized ? '+' : '-'}</button>
          <button type="button" onClick={onClose} className="rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-800">X</button>
        </div>
      </div>

      <div className="relative bg-black" style={videoShellStyle} onClick={() => (showControls ? setShowControls(false) : bumpControls())}>
        <video ref={videoRef} className="h-full w-full object-contain" playsInline muted={isMuted} preload="metadata" />

        {error && <div className="absolute inset-0 grid place-items-center bg-black/70 px-4 text-center text-xs text-rose-200">{error}</div>}

        {showControls && (
          <div className="absolute inset-0 flex flex-col justify-between bg-gradient-to-t from-black/80 via-black/20 to-black/50 p-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 rounded bg-black/55 px-2 py-1 text-[11px] text-slate-200">
                <p className="truncate font-semibold text-slate-100">{current.file_name || current.stream_id}</p>
                <p className="text-slate-300">{currentIndex + 1}/{queue.length}</p>
              </div>
              <div className="shrink-0 rounded bg-black/55 px-2 py-1 text-[11px] text-slate-200">{current.status}</div>
            </div>

            {!isMinimized && (
              <div className="space-y-2">
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, duration)}
                  step={0.1}
                  value={Math.min(currentTime, Math.max(0, duration))}
                  onChange={(e) => {
                    const v = Number(e.target.value || 0);
                    const video = videoRef.current;
                    if (video) {
                      video.currentTime = v;
                    }
                    setCurrentTime(v);
                    bumpControls();
                  }}
                  className="h-1.5 w-full cursor-pointer accent-emerald-400"
                />

                <div className="flex items-center justify-between text-[11px] text-slate-200">
                  <span>{formatClock(currentTime)}</span>
                  <span>{formatClock(duration)}</span>
                </div>

                <div className="grid grid-cols-5 gap-1 text-xs">
                  <button type="button" onClick={goPrev} className="rounded bg-black/55 px-2 py-1 text-slate-100 hover:bg-black/80">Prev</button>
                  <button type="button" onClick={togglePlay} className="rounded bg-emerald-500/85 px-2 py-1 font-semibold text-slate-950 hover:bg-emerald-400">{isPlaying ? 'Pause' : 'Play'}</button>
                  <button type="button" onClick={goNext} className="rounded bg-black/55 px-2 py-1 text-slate-100 hover:bg-black/80">Next</button>
                  <button type="button" onClick={toggleMute} className="rounded bg-black/55 px-2 py-1 text-slate-100 hover:bg-black/80">{isMuted ? 'Unmute' : 'Mute'}</button>
                  <button type="button" onClick={cycleRepeat} className="rounded bg-black/55 px-2 py-1 text-slate-100 hover:bg-black/80">Repeat: {repeatMode}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function App(): JSX.Element {
  const [auth, setAuth] = useState<AuthSnapshot>(readAuth());
  const [section, setSection] = useState<AppSection>(() => sectionFromPath(window.location.pathname));
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [profileOpen, setProfileOpen] = useState<boolean>(false);
  const [streamQuery, setStreamQuery] = useState<string>('');
  const [streamPage, setStreamPage] = useState<number>(1);
  const [apiKeyQuery, setApiKeyQuery] = useState<string>('');
  const [apiKeyPage, setApiKeyPage] = useState<number>(1);
  const [showAddApiKey, setShowAddApiKey] = useState<boolean>(false);
  const [newApiKeyName, setNewApiKeyName] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>(auth.user.name || '');
  const [avatarDataUrl, setAvatarDataUrl] = useState<string>(auth.user.avatar_url || '');
  const [streams, setStreams] = useState<StreamItem[]>([]);
  const [streamsLoading, setStreamsLoading] = useState<boolean>(false);
  const [streamsError, setStreamsError] = useState<string>('');
  const [playerQueue, setPlayerQueue] = useState<StreamItem[]>([]);
  const [playerIndex, setPlayerIndex] = useState<number>(0);
  const [playerVisible, setPlayerVisible] = useState<boolean>(false);
  const [apiKeys, setApiKeys] = useState<ApiKeyItem[]>(buildMockApiKeys());

  useEffect(() => {
    const timer = window.setInterval(() => setAuth(readAuth()), 3000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setDisplayName(auth.user.name || '');
  }, [auth.user.name]);

  useEffect(() => {
    const path = pathFromSection(section);
    if (window.location.pathname !== path) {
      window.history.replaceState(null, '', path);
    }
  }, [section]);

  useEffect(() => {
    let cancelled = false;

    async function loadStreams(): Promise<void> {
      setStreamsLoading(true);
      setStreamsError('');
      try {
        const token = localStorage.getItem('auth_token') || '';
        const headers: Record<string, string> = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const resp = await fetch('/api/v1/app/streams', { method: 'GET', headers });
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const data = (await resp.json()) as { items?: Array<{
          stream_id: string;
          status: string;
          video_source_url?: string;
          file_name?: string;
          thumbnail_url?: string;
          worker_play_url?: string;
          duration_seconds?: number;
          file_size_bytes?: number;
          created_at: string;
        }> };

        const mapped: StreamItem[] = (data.items || []).map((item) => ({
          stream_id: String(item.stream_id || ''),
          status: String(item.status || 'processing'),
          video_source_url: item.video_source_url || '',
          file_name: item.file_name || '',
          thumbnail_url: item.thumbnail_url || '',
          worker_play_url: item.worker_play_url || '',
          duration_seconds: Number(item.duration_seconds || 0),
          file_size_bytes: Number(item.file_size_bytes || 0),
          created_at: String(item.created_at || new Date().toISOString()),
        }));

        if (!cancelled) {
          setStreams(mapped);
        }
      } catch (err) {
        if (!cancelled) {
          setStreamsError(err instanceof Error ? err.message : 'Failed to load streams');
        }
      } finally {
        if (!cancelled) {
          setStreamsLoading(false);
        }
      }
    }

    loadStreams();
    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = useMemo(() => {
    const success = streams.filter((s) => s.status === 'ready').length;
    const failed = streams.filter((s) => s.status === 'failed').length;
    const totalDuration = streams.reduce((sum, s) => sum + s.duration_seconds, 0);
    const totalFileSize = streams.reduce((sum, s) => sum + s.file_size_bytes, 0);
    return { success, failed, totalDuration, totalFileSize };
  }, [streams]);

  const filteredStreams = useMemo(() => {
    const q = streamQuery.trim().toLowerCase();
    if (!q) {
      return streams;
    }
    return streams.filter((s) =>
      s.stream_id.toLowerCase().includes(q) ||
      s.status.toLowerCase().includes(q) ||
      String(s.file_name || '').toLowerCase().includes(q)
    );
  }, [streamQuery, streams]);

  const filteredApiKeys = useMemo(() => {
    const q = apiKeyQuery.trim().toLowerCase();
    if (!q) {
      return apiKeys;
    }
    return apiKeys.filter((k) =>
      k.name.toLowerCase().includes(q) ||
      k.key_id.toLowerCase().includes(q)
    );
  }, [apiKeyQuery, apiKeys]);

  const streamsPageSize = 25;
  const apiPageSize = 25;
  const streamPageCount = Math.max(1, Math.ceil(filteredStreams.length / streamsPageSize));
  const apiPageCount = Math.max(1, Math.ceil(filteredApiKeys.length / apiPageSize));

  useEffect(() => {
    if (streamPage > streamPageCount) {
      setStreamPage(streamPageCount);
    }
  }, [streamPage, streamPageCount]);

  useEffect(() => {
    if (apiKeyPage > apiPageCount) {
      setApiKeyPage(apiPageCount);
    }
  }, [apiKeyPage, apiPageCount]);

  const streamsPageRows = filteredStreams.slice((streamPage - 1) * streamsPageSize, streamPage * streamsPageSize);
  const apiPageRows = filteredApiKeys.slice((apiKeyPage - 1) * apiPageSize, apiKeyPage * apiPageSize);

  const accountLabel = auth.user.name || trimEmail(auth.user.email || '');
  const emailLabel = trimEmail(auth.user.email || '');

  function updateLocalUser(next: Partial<AuthUser>): void {
    const merged: AuthUser = {
      ...auth.user,
      ...next,
    };
    localStorage.setItem('auth_user', JSON.stringify(merged));
    setAuth((prev) => ({ ...prev, user: merged }));
  }

  function onAddApiKey(): void {
    const name = newApiKeyName.trim();
    if (!name) {
      return;
    }
    const now = new Date().toISOString();
    setApiKeys((prev) => [
      {
        key_id: `key_${Math.random().toString(16).slice(2, 9)}`,
        name,
        value_preview: `sk_live_${Math.random().toString(16).slice(2, 6)}...`,
        created_at: now,
      },
      ...prev,
    ]);
    setNewApiKeyName('');
    setShowAddApiKey(false);
    setSection('api-keys');
    setApiKeyPage(1);
  }

  function onAvatarPicked(file: File | null): void {
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const data = String(reader.result || '');
      setAvatarDataUrl(data);
      updateLocalUser({ avatar_url: data });
    };
    reader.readAsDataURL(file);
  }

  function launchPlayerFromStream(streamID: string): void {
    const clicked = streams.find((s) => s.stream_id === streamID);
    if (!clicked) {
      return;
    }

    const playableQueue = streams.filter((s) => Boolean((s.worker_play_url && s.worker_play_url.trim()) || (s.video_source_url && s.video_source_url.trim())));
    if (playableQueue.length === 0) {
      return;
    }

    const index = playableQueue.findIndex((s) => s.stream_id === streamID);
    setPlayerQueue(playableQueue);
    setPlayerIndex(index >= 0 ? index : 0);
    setPlayerVisible(true);
  }

  const menuItems: Array<{ key: AppSection; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'streams', label: 'Streams' },
    { key: 'api-keys', label: 'API Keys' },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="relative flex min-h-screen w-full overflow-x-hidden">
        <div className={`fixed inset-0 z-30 bg-slate-950/70 transition md:hidden ${sidebarOpen ? 'block' : 'hidden'}`} onClick={() => setSidebarOpen(false)} />

        <aside className={`fixed inset-y-0 left-0 z-40 w-72 border-r border-slate-800 bg-slate-900/95 p-4 transition-transform md:static md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="mb-6">
            <LogoMark />
          </div>

          <nav className="space-y-2">
            {menuItems.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => {
                  setSection(m.key);
                  setSidebarOpen(false);
                }}
                className={`w-full rounded-lg px-4 py-2 text-left text-sm ${section === m.key ? 'bg-emerald-500 text-slate-950 font-semibold' : 'text-slate-300 hover:bg-slate-800'}`}
              >
                {m.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="flex min-h-screen w-full flex-col">
          <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-800 bg-slate-950/90 px-4 py-3 backdrop-blur md:px-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="rounded-lg border border-slate-700 p-2 text-slate-200 hover:bg-slate-900"
                onClick={() => setSidebarOpen((v) => !v)}
                aria-label="Toggle sidebar"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              <div>
                <p className="text-sm text-slate-400">User Workspace</p>
                <p className="text-base font-semibold">{menuItems.find((m) => m.key === section)?.label}</p>
              </div>
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => setProfileOpen((v) => !v)}
                className="flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 hover:bg-slate-900"
              >
                <div className="h-7 w-7 overflow-hidden rounded-full bg-emerald-500/20">
                  {avatarDataUrl ? <img src={avatarDataUrl} alt="avatar" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-xs font-semibold text-emerald-300">{(auth.user.name || auth.user.email || 'U').slice(0, 1).toUpperCase()}</div>}
                </div>
                <span className="hidden text-sm text-slate-200 sm:inline">{accountLabel}</span>
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-xl border border-slate-700 bg-slate-900 p-2 shadow-2xl">
                  <a href="/" className="block rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-slate-800">Home</a>
                  <button type="button" onClick={() => setSection('settings')} className="mt-1 block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800">Profile settings</button>
                  <a href="/signout" className="mt-1 block rounded-lg px-3 py-2 text-sm text-rose-300 hover:bg-slate-800">Sign out</a>
                </div>
              )}
            </div>
          </header>

          <main className="w-full flex-1 px-4 pb-24 pt-5 md:px-6 md:pb-28">
            {section === 'overview' && (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                    <p className="text-xs text-slate-400">Success Streams</p>
                    <p className="mt-2 text-2xl font-semibold text-emerald-300">{metrics.success}</p>
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                    <p className="text-xs text-slate-400">Failed Streams</p>
                    <p className="mt-2 text-2xl font-semibold text-rose-300">{metrics.failed}</p>
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                    <p className="text-xs text-slate-400">Total Duration</p>
                    <p className="mt-2 text-2xl font-semibold">{formatDuration(metrics.totalDuration)}</p>
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                    <p className="text-xs text-slate-400">Total File Size</p>
                    <p className="mt-2 text-2xl font-semibold">{formatBytes(metrics.totalFileSize)}</p>
                  </div>
                </div>
              </div>
            )}

            {section === 'streams' && (
              <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <input
                    value={streamQuery}
                    onChange={(e) => {
                      setStreamQuery(e.target.value);
                      setStreamPage(1);
                    }}
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-emerald-400 focus:outline-none md:max-w-sm"
                    placeholder="Filter by stream id or status"
                  />
                  <p className="text-xs text-slate-400">Showing {streamsPageRows.length} of {filteredStreams.length} (max 25/page)</p>
                </div>

                {streamsLoading && <p className="mb-3 text-sm text-slate-400">Loading streams...</p>}
                {streamsError && <p className="mb-3 text-sm text-rose-300">Could not load streams: {streamsError}</p>}

                <div className="space-y-3">
                  {streamsPageRows.map((s) => (
                    <div
                      key={s.stream_id}
                      className={`flex flex-col gap-3 rounded-lg border border-slate-800 bg-slate-950/70 p-3 md:flex-row md:items-center md:justify-between ${s.worker_play_url || s.video_source_url ? 'cursor-pointer hover:border-emerald-500/50' : ''}`}
                      onClick={() => launchPlayerFromStream(s.stream_id)}
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={s.thumbnail_url || '/ssr-static/favicon.svg'}
                          alt="thumbnail"
                          className="h-16 w-24 rounded-md bg-slate-800 object-cover"
                        />
                        <div>
                          <p className="text-sm font-semibold">{s.file_name || s.stream_id}</p>
                          <p className="text-xs text-slate-500">{s.stream_id}</p>
                          <p className="text-xs text-slate-400">{new Date(s.created_at).toLocaleString()} | {formatDuration(s.duration_seconds)} | {formatBytes(s.file_size_bytes)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-1 text-xs ${s.status === 'ready' ? 'bg-emerald-500/20 text-emerald-300' : s.status === 'failed' ? 'bg-rose-500/20 text-rose-300' : 'bg-amber-500/20 text-amber-300'}`}>{s.status}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            launchPlayerFromStream(s.stream_id);
                          }}
                          className={`rounded-lg border border-slate-700 px-3 py-1 text-xs ${s.worker_play_url || s.video_source_url ? 'hover:bg-slate-900' : 'pointer-events-none opacity-60'}`}
                        >
                          Play
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex items-center justify-between text-sm">
                  <button type="button" disabled={streamPage <= 1} onClick={() => setStreamPage((p) => Math.max(1, p - 1))} className="rounded-lg border border-slate-700 px-3 py-1 disabled:opacity-50">Prev</button>
                  <span className="text-slate-400">Page {streamPage} / {streamPageCount}</span>
                  <button type="button" disabled={streamPage >= streamPageCount} onClick={() => setStreamPage((p) => Math.min(streamPageCount, p + 1))} className="rounded-lg border border-slate-700 px-3 py-1 disabled:opacity-50">Next</button>
                </div>
              </div>
            )}

            {section === 'api-keys' && (
              <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <input
                    value={apiKeyQuery}
                    onChange={(e) => {
                      setApiKeyQuery(e.target.value);
                      setApiKeyPage(1);
                    }}
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-emerald-400 focus:outline-none md:max-w-sm"
                    placeholder="Filter by key id or name"
                  />
                  <button type="button" onClick={() => setShowAddApiKey(true)} className="rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400">+ Add</button>
                </div>

                <div className="space-y-3">
                  {apiPageRows.map((k) => (
                    <div key={k.key_id} className="flex flex-col gap-3 rounded-lg border border-slate-800 bg-slate-950/70 p-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-sm font-semibold">{k.name}</p>
                        <p className="text-xs text-slate-400">{k.key_id} | {k.value_preview} | {new Date(k.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" className="rounded-lg border border-slate-700 px-3 py-1 text-xs hover:bg-slate-900">Rotate</button>
                        <button type="button" onClick={() => setApiKeys((prev) => prev.filter((x) => x.key_id !== k.key_id))} className="rounded-lg border border-rose-700/70 px-3 py-1 text-xs text-rose-200 hover:bg-rose-900/20">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex items-center justify-between text-sm">
                  <button type="button" disabled={apiKeyPage <= 1} onClick={() => setApiKeyPage((p) => Math.max(1, p - 1))} className="rounded-lg border border-slate-700 px-3 py-1 disabled:opacity-50">Prev</button>
                  <span className="text-slate-400">Page {apiKeyPage} / {apiPageCount}</span>
                  <button type="button" disabled={apiKeyPage >= apiPageCount} onClick={() => setApiKeyPage((p) => Math.min(apiPageCount, p + 1))} className="rounded-lg border border-slate-700 px-3 py-1 disabled:opacity-50">Next</button>
                </div>
              </div>
            )}

            {section === 'settings' && (
              <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                <h2 className="text-lg font-semibold">Profile Settings</h2>
                <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="h-20 w-20 overflow-hidden rounded-full bg-slate-800">
                    {avatarDataUrl ? <img src={avatarDataUrl} alt="avatar" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-lg font-semibold text-slate-300">{(auth.user.name || auth.user.email || 'U').slice(0, 1).toUpperCase()}</div>}
                  </div>
                  <label className="inline-flex cursor-pointer items-center rounded-lg border border-slate-700 px-3 py-2 text-sm hover:bg-slate-900">
                    Upload/Replace avatar
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onAvatarPicked(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
                  </label>
                </div>

                <div className="mt-4 max-w-md">
                  <label className="mb-1 block text-xs text-slate-400">Display name</label>
                  <input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-emerald-400 focus:outline-none"
                    placeholder="Your display name"
                  />
                  <button
                    type="button"
                    onClick={() => updateLocalUser({ name: displayName.trim() })}
                    className="mt-3 rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400"
                  >
                    Save name
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>

        <a href={auth.user.role === 'admin' ? '/admin' : '#'} className={`fixed inset-x-0 bottom-0 z-30 flex items-center justify-between border-t border-slate-800 bg-slate-950/95 px-4 py-3 backdrop-blur md:inset-x-6 md:bottom-4 md:rounded-xl md:border ${auth.user.role === 'admin' ? 'cursor-pointer hover:bg-slate-900' : 'cursor-default'}`}>
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 overflow-hidden rounded-full bg-slate-800">
              {avatarDataUrl ? <img src={avatarDataUrl} alt="avatar" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-xs font-semibold text-slate-300">{(auth.user.name || auth.user.email || 'U').slice(0, 1).toUpperCase()}</div>}
            </div>
            <p className="max-w-[180px] truncate text-sm text-slate-300">{emailLabel}</p>
          </div>
          <span className={`rounded-full px-2 py-1 text-xs ${auth.user.role === 'admin' ? 'bg-emerald-500/25 text-emerald-300' : 'bg-slate-700 text-slate-200'}`}>{auth.user.role === 'admin' ? 'admin' : 'user'}</span>
        </a>

        {playerVisible && playerQueue.length > 0 && (
          <FloatingVideoPlayer
            queue={playerQueue}
            currentIndex={playerIndex}
            onIndexChange={(next) => setPlayerIndex(clamp(next, 0, Math.max(0, playerQueue.length - 1)))}
            onClose={() => setPlayerVisible(false)}
          />
        )}

        {showAddApiKey && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/80 p-4">
            <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
              <h3 className="text-lg font-semibold">Create API key</h3>
              <p className="mt-1 text-sm text-slate-400">Give this key a readable name for easier rotation later.</p>
              <input
                value={newApiKeyName}
                onChange={(e) => setNewApiKeyName(e.target.value)}
                className="mt-4 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-emerald-400 focus:outline-none"
                placeholder="e.g. mobile-app-prod"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setShowAddApiKey(false)} className="rounded-lg border border-slate-700 px-3 py-2 text-sm hover:bg-slate-800">Cancel</button>
                <button type="button" onClick={onAddApiKey} className="rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400">Create key</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const root = document.getElementById('root');
if (root) {
  createRoot(root).render(<App />);
}
